/**
 * Floating toast stack (bottom-right) that shows the current user's background
 * project initialisations with live progress.  Visible across all pages, survives
 * refresh, only visible to the user who kicked the job off.
 *
 * Generic in spirit: each toast is rendered by {@link BackgroundJobToast} which
 * only cares about {title, progress, state}.  Extending to other job types is a
 * matter of adding a sibling data source.
 */

import { useCallback, useEffect, useState } from "react";
import { SuccessIcon, SpinnerIcon, AlertIcon, CloseIcon, UncheckedIcon } from "@/lib/icons";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMyInitialisations,
  type InitialisationProgress,
} from "@/hooks/useMyInitialisations";
import { useCancelInitialization } from "@/components/projects/hooks/useInitialiseProject";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toFinnishLabel } from "@/lib/initStepLabels";

type ToastState = "running" | "success" | "error";

interface BackgroundJobToastProps {
  title: string;
  progress: InitialisationProgress | null;
  state: ToastState;
  onDismiss: () => void;
  onCancel?: () => void;
  cancelling?: boolean;
}

function BackgroundJobToast({
  title,
  progress,
  state,
  onDismiss,
  onCancel,
  cancelling,
}: BackgroundJobToastProps) {
  // Single-step jobs (totalSteps <= 1, e.g. the substitute refresh) have no
  // meaningful step count, so drive the bar from item progress instead — a
  // step-based bar would sit at 100% the whole time. Multi-step jobs (project
  // init, 8 steps) keep the step-based bar.
  const singleStep = !!progress && progress.totalSteps <= 1;
  const percent = !progress
    ? 0
    : singleStep
    ? progress.itemsTotal > 0
      ? Math.round((progress.itemsDone / progress.itemsTotal) * 100)
      : 0
    : Math.round((progress.step / progress.totalSteps) * 100);

  const Icon =
    state === "success" ? SuccessIcon : state === "error" ? AlertIcon : SpinnerIcon;
  const iconClass =
    state === "success"
      ? "text-green-600"
      : state === "error"
      ? "text-destructive"
      : "text-muted-foreground animate-spin";

  return (
    <div className="w-80 rounded-lg border bg-background shadow-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${iconClass}`} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{title}</p>
          <p className="text-xs text-muted-foreground">
            {state === "success"
              ? "Valmis"
              : state === "error"
              ? progress?.error ?? "Virhe"
              : toFinnishLabel(progress?.stepLabel)}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-6 w-6 p-0 shrink-0"
          aria-label="Sulje"
        >
          <CloseIcon className="h-3 w-3" />
        </Button>
      </div>

      {state === "running" && (
        <>
          {progress?.detail && (
            <p className="text-[11px] text-muted-foreground truncate">
              {progress.detail}
            </p>
          )}
          <Progress value={percent} className="h-1.5" />
          <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="truncate">
              {progress && progress.itemsTotal > 0
                ? `${progress.itemsDone}/${progress.itemsTotal}`
                : ""}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {progress && !singleStep && (
                <span className="tabular-nums">
                  Vaihe {progress.step}/{progress.totalSteps}
                </span>
              )}
              {onCancel && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  disabled={cancelling}
                  className="h-6 px-2 text-[11px] text-destructive hover:text-destructive"
                  aria-label="Peruuta taustatyö"
                >
                  {cancelling ? (
                    <SpinnerIcon className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <UncheckedIcon className="h-3 w-3 mr-1" fill="currentColor" />
                      Peruuta
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function BackgroundJobsToasts() {
  const { isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  const { cancelInitialization } = useCancelInitialization();

  const { items } = useMyInitialisations({
    enabled: isAuthenticated,
    onComplete: (operationId, success) => {
      if (!success) return;
      // Auto-dismiss successful jobs after 3s so the user sees the green check.
      setTimeout(() => {
        setDismissed((prev) => new Set(prev).add(operationId));
      }, 3000);
    },
  });

  const dismiss = useCallback((operationId: string) => {
    setDismissed((prev) => new Set(prev).add(operationId));
  }, []);

  const cancel = useCallback(
    async (operationId: string) => {
      setCancelling((prev) => new Set(prev).add(operationId));
      try {
        await cancelInitialization(operationId);
      } catch {
        // Worker will either stop or not; the stream reflects final state.
      } finally {
        setCancelling((prev) => {
          const next = new Set(prev);
          next.delete(operationId);
          return next;
        });
      }
    },
    [cancelInitialization]
  );

  // Reset dismissed set if an opId disappears from the backend list (worker
  // already unregistered it) so stale entries don't leak memory.
  useEffect(() => {
    setDismissed((prev) => {
      const known = new Set(items.map((i) => i.operationId));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (known.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [items]);

  const visible = items.filter((i) => !dismissed.has(i.operationId));
  if (visible.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {visible.map((item) => {
        const p = item.progress;
        const state: ToastState = p?.error
          ? "error"
          : p?.complete
          ? "success"
          : "running";
        const canCancel = item.cancellable !== false;
        return (
          <BackgroundJobToast
            key={item.operationId}
            title={item.projectName}
            progress={p}
            state={state}
            onDismiss={() => dismiss(item.operationId)}
            onCancel={
              state === "running" && canCancel
                ? () => cancel(item.operationId)
                : undefined
            }
            cancelling={cancelling.has(item.operationId)}
          />
        );
      })}
    </div>
  );
}
